// EIP-712 signing through the connected wallet, shared by every venue whose
// onboarding is "sign this payload the backend built" (Derive, Papertrade).
import { assertManualWalletAction, getWalletProvider } from '@/lib/wallet/connect'

const DEFAULT_NO_SIGNATURE = 'The wallet returned no signature. Approve the prompt and try again.'

// Some wallets resolve `null` when the customer dismisses a prompt instead of
// rejecting it, and an undefined signature reaches the backend as a request
// that cannot succeed — burning the single-use prepare.
export function requireSignature(result: unknown, message: string = DEFAULT_NO_SIGNATURE): string {
  if (typeof result !== 'string' || !result.startsWith('0x')) {
    throw new Error(message)
  }
  return result
}

/**
 * `eth_signTypedData_v4` over `typedDataJson`, which goes to the wallet
 * verbatim: the backend recovers the signer against the payload it persisted at
 * prepare time, so rebuilding it here could only diverge from what is being
 * authorised. The caller switches the wallet to the payload's chain first.
 */
export async function signTypedDataV4(
  walletAddress: string,
  typedDataJson: string,
  noSignatureMessage?: string
): Promise<string> {
  assertManualWalletAction()
  const provider = await getWalletProvider()
  return requireSignature(
    await provider.request({
      method: 'eth_signTypedData_v4',
      params: [walletAddress as `0x${string}`, typedDataJson],
    }),
    noSignatureMessage
  )
}
